// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { CustomAppState } from "@bg/App/Store/Reducers";
import { parseBoolean } from "@bg/XDL/Utilities/helpers";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
};
const mapStateToProps = (state: CustomAppState) => ({
    account: state.pages.accountSettings.editingAccount,
    billTrustEmailConfirmations: state.xAccountSettings?.properties?.billTrustEmailConfirmations,
    billTrustEmailNotify: state.xAccountSettings?.properties?.billTrustEmailNotify,
    billTrustPdfAttachment: state.xAccountSettings?.properties?.billTrustPdfAttachment,
    billTrustProperties: state.xAccountSettings?.properties,
});
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export interface AccountSettingsManageBillTrustStyles {
    manageBillTrustCheckboxGroup: CheckboxGroupComponentProps;
    manageBillTrustCheckbox: CheckboxProps;
    manageBillTrustTitle: TypographyProps;
}
export const manageBillTrustStyles: AccountSettingsManageBillTrustStyles = {
    manageBillTrustTitle: {
        variant: "h4",
        as: "h2",
    },
    manageBillTrustCheckbox: {
        typographyProps: {
            css: css`
                margin-left: 10px;
            `,
        },
    },
    manageBillTrustCheckboxGroup: {},
};
const styles = manageBillTrustStyles;
const AccountSettingsManageBillTrust = ({
    account,
    updateAccountSettings,
    billTrustEmailConfirmations,
    billTrustEmailNotify,
    billTrustPdfAttachment,
    billTrustProperties,
}: Props) => {
    const billTrustToggle = (type: string, value: boolean) => {
        const updatedProperties: any = { ...billTrustProperties, [type]: value };
        updateAccountSettings({
            properties: updatedProperties,
        } as any);
    };
    if (!account || (!billTrustEmailConfirmations && !billTrustEmailNotify && !billTrustPdfAttachment)) {
        return null;
    }
    return (
        <>
            <Typography {...styles.manageBillTrustTitle}>{translate("Manage BillTrust")}</Typography>
            <CheckboxGroup {...styles.manageBillTrustCheckboxGroup}>
                {billTrustEmailConfirmations && (
                    <Checkbox
                        {...styles.manageBillTrustCheckbox}
                        onChange={(_, value) => {
                            billTrustToggle("billTrustEmailConfirmations", value);
                        }}
                        checked={parseBoolean(billTrustEmailConfirmations)}
                        data-test-selector="accountSettings_billTrustEmailConfirmations"
                    >
                        {translate("BillTrust Email Confirmations")}
                    </Checkbox>
                )}
                {billTrustEmailNotify && (
                    <Checkbox
                        {...styles.manageBillTrustCheckbox}
                        onChange={(_, value) => {
                            billTrustToggle("billTrustEmailNotify", value);
                        }}
                        checked={parseBoolean(billTrustEmailNotify)}
                        data-test-selector="accountSettings_billTrustEmailNotify"
                    >
                        {translate("BillTrust Email Notify")}
                    </Checkbox>
                )}
                {billTrustPdfAttachment && (
                    <Checkbox
                        {...styles.manageBillTrustCheckbox}
                        onChange={(_, value) => {
                            billTrustToggle("billTrustPdfAttachment", value);
                        }}
                        checked={parseBoolean(billTrustPdfAttachment)}
                        data-test-selector="accountSettings_billTrustPdfAttachment"
                    >
                        {translate("BillTrust Pdf Attachment")}
                    </Checkbox>
                )}
            </CheckboxGroup>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsManageBillTrust),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "Manage BillTrust",
    },
};
export default widgetModule;
